import { useState, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { VenueSelection } from '../../types/map';

interface ReportingStepProps {
  venueSelections: VenueSelection[];
  reportingTier: 'basic' | 'professional' | 'enterprise' | undefined;
  reportingOptions: {
    liveReporting?: boolean;
    machineBracket?: string;
    skuTier?: string;
  };
  onReportingTierChange: (tier: 'basic' | 'professional' | 'enterprise' | undefined) => void;
  onReportingOptionsChange: (options: {
    liveReporting?: boolean;
    machineBracket?: string;
    skuTier?: string;
  }) => void;
}

const getMachineBracket = (machineCount: number): string => {
  if (machineCount <= 25) return '1-25';
  if (machineCount <= 100) return '26-100';
  if (machineCount <= 250) return '101-250';
  return '251+';
};

const getMachineBracketPrice = (tier: string | null, bracket: string): number => {
  if (!tier) return 0;
  const pricing = {
    basic: { '1-25': 250, '26-100': 500, '101-250': 750, '251+': 1000 },
    professional: { '1-25': 500, '26-100': 1000, '101-250': 1500, '251+': 2000 },
    enterprise: { '1-25': 1000, '26-100': 2000, '101-250': 3000, '251+': 4000 },
  };
  return pricing[tier as keyof typeof pricing][bracket as keyof typeof pricing.basic] || 0;
};

const calculatePricing = (
  tier: string | null,
  machineBracket: string,
  skuTier: string,
  liveReporting: boolean = false
) => {
  if (!tier) {
    return { total: 0, basePrice: 0, skuAddOn: 0, liveReportingAddOn: 0, machineBracket };
  }
  const pricing = {
    basic: { '1-25': 250, '26-100': 500, '101-250': 750, '251+': 1000 },
    professional: { '1-25': 500, '26-100': 1000, '101-250': 1500, '251+': 2000 },
    enterprise: { '1-25': 1000, '26-100': 2000, '101-250': 3000, '251+': 4000 },
  };

  const basePrice = pricing[tier as keyof typeof pricing][machineBracket as keyof typeof pricing.basic];
  let skuAddOn = 0;

  if (skuTier === '11-20') skuAddOn = 500;
  else if (skuTier === '21+')
    return {
      total: 'Custom',
      basePrice,
      skuAddOn: 'Contact Sales',
      liveReportingAddOn: 0,
      machineBracket,
      skuTier,
    };

  // Enterprise tier always requires custom pricing
  if (tier === 'enterprise') {
    return {
      total: 'Custom',
      basePrice: 'Contact Sales',
      skuAddOn: 'Contact Sales',
      liveReportingAddOn: 'Contact Sales',
      machineBracket,
      skuTier,
    };
  }

  // Live reporting add-on pricing
  let liveReportingAddOn = 0;
  if (liveReporting) {
    if (tier === 'basic') liveReportingAddOn = 250;
    else if (tier === 'professional') liveReportingAddOn = 750;
  }

  const total = basePrice + skuAddOn + liveReportingAddOn;
  return { total, basePrice, skuAddOn, liveReportingAddOn, machineBracket, skuTier };
};

const tierData = {
  basic: {
    name: 'Basic',
    description: 'Aggregated metrics, real-time dashboard, PDF reports',
    features: [
      'Total monthly sales in £',
      'Total monthly sales in units',
      '5 CSV exports',
    ],
    basePrice: 250,
  },
  professional: {
    name: 'Professional',
    description: 'Segmented metrics, alerts, interactive reports',
    features: [
      'Everything in Basic',
      'Total monthly sales per SKU',
      'Total monthly sales per machine',
      'Planogram availability',
      'Distribution by machine and gym',
      'Sales by time and region',
      'Total and category market share',
      'Conversion rate per SKU',
      '10 CSV exports',
    ],
    basePrice: 500,
  },
  enterprise: {
    name: 'Enterprise',
    description: 'Custom insights, predictive analytics, unlimited reports',
    features: [
      'A/B testing',
      'Price elasticity testing',
      'Vision and demographic reporting',
      'Heat map and behavior tracking',
      'Funnel and conversion reporting',
      'Additional custom analytics',
    ],
    basePrice: null, // Custom pricing
  },
};

export function ReportingStep({
  venueSelections,
  reportingTier,
  reportingOptions,
  onReportingTierChange,
  onReportingOptionsChange,
}: ReportingStepProps) {
  const totalMachines = venueSelections.reduce((sum, vs) => sum + vs.machineCount, 0);
  const machineBracket = getMachineBracket(totalMachines);
  const [isMachineDropdownOpen, setIsMachineDropdownOpen] = useState(false);
  const [isSkuDropdownOpen, setIsSkuDropdownOpen] = useState(false);

  // Initialize machine bracket if not set
  useEffect(() => {
    if (!reportingOptions.machineBracket && totalMachines > 0) {
      onReportingOptionsChange({
        ...reportingOptions,
        machineBracket: '26-100',
      });
    }
  }, [totalMachines, reportingOptions]);

  const handleTierChange = (value: string) => {
    if (value === 'none') {
      onReportingTierChange(undefined);
    } else {
      onReportingTierChange(value as 'basic' | 'professional' | 'enterprise');
    }
  };

  const handleMachineBracketChange = (bracket: string) => {
    onReportingOptionsChange({
      ...reportingOptions,
      machineBracket: bracket,
    });
    setIsMachineDropdownOpen(false);
  };

  const handleSkuTierChange = (tier: string) => {
    onReportingOptionsChange({
      ...reportingOptions,
      skuTier: tier,
    });
    setIsSkuDropdownOpen(false);
  };

  const handleLiveReportingChange = (enabled: boolean) => {
    onReportingOptionsChange({
      ...reportingOptions,
      liveReporting: enabled,
    });
  };

  const currentMachineBracket = reportingOptions.machineBracket || '26-100';
  const currentSkuTier = reportingOptions.skuTier || '1-10';
  const currentLiveReporting = reportingOptions.liveReporting || false;

  const selectedTierData = reportingTier ? tierData[reportingTier] : null;
  const pricing = calculatePricing(
    reportingTier || null,
    currentMachineBracket,
    currentSkuTier,
    currentLiveReporting
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Reporting & Analytics</h2>
        <p className="text-slate-600">
          Select a reporting tier and options based on your machine count and SKU requirements.
        </p>
      </div>

      {/* Step 1: Select Your Reporting Tier */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Step 1: Select Your Reporting Tier</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch min-h-[600px]">
          {/* Left Side - Plan Selection List */}
          <div className="flex flex-col h-full min-h-[600px]">
            <RadioGroup
              value={reportingTier || 'none'}
              onValueChange={handleTierChange}
              className="flex flex-col gap-4 h-full"
            >
              {/* Basic Plan */}
              <div 
                className={`flex items-center justify-between p-6 border rounded-lg transition-all cursor-pointer flex-1 ${
                  reportingTier === 'basic'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
                onClick={() => handleTierChange('basic')}
              >
                <div className="flex items-center space-x-4 flex-1">
                  <RadioGroupItem value="basic" id="basic" className="w-5 h-5 flex-shrink-0" />
                  <Label htmlFor="basic" className="cursor-pointer flex-1">
                    <div className="text-lg font-semibold text-gray-900">Basic</div>
                    <div className="text-sm text-gray-500 mt-1">1x Account</div>
                  </Label>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <div className="text-xl font-bold text-gray-900">£500</div>
                  <div className="text-sm text-gray-500">/month</div>
                </div>
              </div>

              {/* Professional Plan */}
              <div 
                className={`flex items-center justify-between p-6 border rounded-lg transition-all cursor-pointer flex-1 ${
                  reportingTier === 'professional'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
                onClick={() => handleTierChange('professional')}
              >
                <div className="flex items-center space-x-4 flex-1">
                  <RadioGroupItem value="professional" id="professional" className="w-5 h-5 flex-shrink-0" />
                  <Label htmlFor="professional" className="cursor-pointer flex-1">
                    <div className="text-lg font-semibold text-gray-900">Professional</div>
                    <div className="text-sm text-gray-500 mt-1">3x Account</div>
                  </Label>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <div className="text-xl font-bold text-gray-900">£1000</div>
                  <div className="text-sm text-gray-500">/month</div>
                </div>
              </div>

              {/* Enterprise Plan */}
              <div 
                className={`flex items-center justify-between p-6 border rounded-lg transition-all cursor-pointer flex-1 ${
                  reportingTier === 'enterprise'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
                onClick={() => handleTierChange('enterprise')}
              >
                <div className="flex items-center space-x-4 flex-1">
                  <RadioGroupItem value="enterprise" id="enterprise" className="w-5 h-5 flex-shrink-0" />
                  <Label htmlFor="enterprise" className="cursor-pointer flex-1">
                    <div className="text-lg font-semibold text-gray-900">Enterprise</div>
                    <div className="text-sm text-gray-500 mt-1">10x Account</div>
                  </Label>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <div className="text-xl font-bold text-gray-900">Contact Sales</div>
                  <div className="text-sm text-gray-500">Custom Pricing</div>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Right Side - Detailed Plan View */}
          {selectedTierData ? (
            <Card className="bg-white border-gray-200 flex flex-col h-full min-h-[600px]">
              <CardContent className="p-6 flex flex-col h-full overflow-y-auto">
                <div className="mb-6 flex-shrink-0">
                  <h3 className="text-2xl font-bold mb-2 text-gray-900">{selectedTierData.name}</h3>
                  <p className="text-gray-600 text-sm">{selectedTierData.description}</p>
                </div>

                <Card className="bg-slate-50 border-gray-200 mb-4 flex-1 flex flex-col min-h-[400px]">
                  <CardContent className="p-6 flex flex-col flex-1 min-h-0">
                    <div className="mb-4 flex-shrink-0">
                      <div className="text-3xl font-bold">
                        {typeof pricing.total === 'number' 
                          ? `£${pricing.total.toLocaleString('en-GB')}`
                          : 'Contact Sales'}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">/month</div>
                    </div>

                    <div className="space-y-2 flex-1 overflow-y-auto min-h-0">
                      {selectedTierData.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <Check className="w-4 h-4 text-gray-900 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-gray-100 border-gray-200 flex flex-col h-full min-h-[600px]">
              <CardContent className="p-6 flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <p className="text-lg font-medium mb-2">Select a plan</p>
                  <p className="text-sm">Choose a reporting tier from the left to see details</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Step 2: Number of Machines */}
      {reportingTier && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Step 2: Number of Machines</h3>
          {reportingTier === 'enterprise' ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <p className="text-lg font-semibold text-blue-900 mb-2">Enterprise Pricing</p>
              <p className="text-gray-700">Please contact sales for custom pricing based on your machine count.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Machine Count Bracket
                </label>
                <div className="relative">
                  <button
                    onClick={() => setIsMachineDropdownOpen(!isMachineDropdownOpen)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg bg-white flex items-center justify-between hover:border-gray-400 transition-colors"
                  >
                    <span className="text-gray-900">
                      {currentMachineBracket === '1-25' && `1-25 machines (£${getMachineBracketPrice(reportingTier, '1-25')})`}
                      {currentMachineBracket === '26-100' && `26-100 machines (£${getMachineBracketPrice(reportingTier, '26-100')})`}
                      {currentMachineBracket === '101-250' && `101-250 machines (£${getMachineBracketPrice(reportingTier, '101-250')})`}
                      {currentMachineBracket === '251+' && `251+ machines (£${getMachineBracketPrice(reportingTier, '251+')})`}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isMachineDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isMachineDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      {[
                        { value: '1-25', label: '1-25 machines' },
                        { value: '26-100', label: '26-100 machines' },
                        { value: '101-250', label: '101-250 machines' },
                        { value: '251+', label: '251+ machines' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleMachineBracketChange(option.value)}
                          className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                            currentMachineBracket === option.value ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-medium">
                  {currentMachineBracket === '1-25' && `Selected: 1-25 machines (£${getMachineBracketPrice(reportingTier, '1-25')})`}
                  {currentMachineBracket === '26-100' && `Selected: 26-100 machines (£${getMachineBracketPrice(reportingTier, '26-100')})`}
                  {currentMachineBracket === '101-250' && `Selected: 101-250 machines (£${getMachineBracketPrice(reportingTier, '101-250')})`}
                  {currentMachineBracket === '251+' && `Selected: 251+ machines (£${getMachineBracketPrice(reportingTier, '251+')})`}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Number of SKUs */}
      {reportingTier && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Step 3: Number of SKUs</h3>
          {reportingTier === 'enterprise' ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <p className="text-lg font-semibold text-blue-900 mb-2">Enterprise SKU Pricing</p>
              <p className="text-gray-700">Please contact sales for custom SKU pricing based on your requirements.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SKU Count Tier
                </label>
                <div className="relative">
                  <button
                    onClick={() => setIsSkuDropdownOpen(!isSkuDropdownOpen)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg bg-white flex items-center justify-between hover:border-gray-400 transition-colors"
                  >
                    <span className="text-gray-900">
                      {currentSkuTier === '1-10' && '1-10 SKUs (Included)'}
                      {currentSkuTier === '11-20' && '11-20 SKUs (+£500/month)'}
                      {currentSkuTier === '21+' && '21+ SKUs (Custom pricing)'}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isSkuDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isSkuDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      {[
                        { value: '1-10', label: '1-10 SKUs (Included)' },
                        { value: '11-20', label: '11-20 SKUs' },
                        { value: '21+', label: '21+ SKUs' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleSkuTierChange(option.value)}
                          className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                            currentSkuTier === option.value ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-medium">
                  {currentSkuTier === '1-10' && 'Selected: 1-10 SKUs (Included in base price)'}
                  {currentSkuTier === '11-20' && 'Selected: 11-20 SKUs (+£500/month add-on)'}
                  {currentSkuTier === '21+' && 'Selected: 21+ SKUs (Custom pricing required)'}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 4: Live Reporting */}
      {reportingTier && reportingTier !== 'enterprise' && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Step 4: Live Reporting</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Enable Live Reporting
              </label>
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <div className="font-semibold text-gray-900">Real-time Live Reporting</div>
                    {currentLiveReporting && (
                      <div className="flex items-center space-x-1">
                        <div className="relative">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <div className="absolute inset-0 w-2 h-2 bg-green-500 rounded-full animate-ping opacity-75"></div>
                        </div>
                        <span className="text-xs font-bold text-green-600 animate-pulse">LIVE</span>
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Get instant updates and real-time data streaming for your analytics
                  </div>
                  <div className="text-sm text-blue-600 mt-2 font-medium">
                    {reportingTier === 'basic' && '+£250/month'}
                    {reportingTier === 'professional' && '+£750/month'}
                  </div>
                </div>
                <button
                  onClick={() => handleLiveReportingChange(!currentLiveReporting)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    currentLiveReporting ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      currentLiveReporting ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">
                {currentLiveReporting ? 'Live reporting enabled' : 'Live reporting disabled'}
                {currentLiveReporting && (
                  <span className="text-blue-600 ml-2">
                    {reportingTier === 'basic' && '(+£250/month)'}
                    {reportingTier === 'professional' && '(+£750/month)'}
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
